import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function RoleRoute({ role, children }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // Only show full-screen loader on initial auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light font-sans">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-navy"></div>
          <p className="text-brand-navy font-medium animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Profile still loading — show a brief non-blocking wait (max ~3s then give up)
  if (!profile) {
    return <ProfileWaiter role={role} location={location}>{children}</ProfileWaiter>;
  }

  // Role check
  const hasAccess = Array.isArray(role)
    ? role.includes(profile.role)
    : profile.role === role;

  if (!hasAccess) {
    if (profile.role === 'admin') return <Navigate to="/admin" replace />;
    if (profile.role === 'driver') return <Navigate to="/driver" replace />;
    return <Navigate to="/app" replace />;
  }

  return children;
}

// Waits up to 3s for profile, then redirects to login if still missing
function ProfileWaiter({ role, location, children }) {
  const { profile } = useAuth();
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 3000);
    return () => clearTimeout(t);
  }, []);

  if (timedOut && !profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profile) {
    const hasAccess = Array.isArray(role)
      ? role.includes(profile.role)
      : profile.role === role;
    if (!hasAccess) {
      if (profile.role === 'admin') return <Navigate to="/admin" replace />;
      if (profile.role === 'driver') return <Navigate to="/driver" replace />;
      return <Navigate to="/app" replace />;
    }
    return children;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-light">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-brand-gold"></div>
    </div>
  );
}